const STORAGE_PREFIX = "softmap:conversation-selection:v1:";
const UUID_V7 = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function normalizedAccountEmail(accountEmail: string): string {
  return accountEmail.trim().toLocaleLowerCase("en-US");
}
function storageKey(accountEmail: string): string | null {
  const email = normalizedAccountEmail(accountEmail);
  return email ? `${STORAGE_PREFIX}${encodeURIComponent(email)}` : null;
}
function isGuest(accountEmail: string, guest: boolean): boolean { return guest || !normalizedAccountEmail(accountEmail); }
function validThreadID(threadID: unknown): threadID is string { return typeof threadID === "string" && UUID_V7.test(threadID); }
function browserStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try { return window.localStorage ?? null; } catch { return null; }
}

/**
 * 按规范化账号隔离最近会话选择。访客没有稳定身份，严格禁止读写 localStorage，
 * 同时只接受 UUIDv7，避免把篡改后的路径值带入显式 Thread 请求。
 */
export function load(accountEmail: string, guest = false): string | null {
  if (isGuest(accountEmail, guest)) return null;
  const key = storageKey(accountEmail);
  const storage = browserStorage();
  if (!key || !storage) return null;
  try {
    const value = storage.getItem(key);
    if (value === null || validThreadID(value)) return value;
    // 篡改或旧格式值必须立即删除，避免每次恢复都重复触发非法显式 Thread 请求。
    storage.removeItem(key);
    return null;
  } catch {
    return null;
  }
}

export function save(accountEmail: string, threadID: string, guest = false): void {
  if (isGuest(accountEmail, guest) || !validThreadID(threadID)) return;
  const key = storageKey(accountEmail);
  const storage = browserStorage();
  if (!key || !storage) return;
  try { storage.setItem(key, threadID); } catch { /* 隐私模式或存储配额失败时仍可正常使用会话。 */ }
}

export function clear(accountEmail: string, guest = false): void {
  if (isGuest(accountEmail, guest)) return;
  const key = storageKey(accountEmail);
  const storage = browserStorage();
  if (!key || !storage) return;
  try { storage.removeItem(key); } catch { /* 清理失败不影响当前内存态。 */ }
}

export const conversationSelection = {load, save, clear};
export {normalizedAccountEmail};
